import React, { useReducer } from "react"
import ReactDOMServer from "react-dom/server"
import parse from "./utils/parse"
import replaceOutlookTags from "../utils/replace-outlook-tags"
import components from "./components"
import ClosestEditableDetector from "./closest-editable-detector"
import { ThemeProvider, themeFromTemplateSettings } from "../hooks/use-theme"
import { EditorContent } from "../editor-containers"

const OverridesContext = React.createContext({})
export const TemplateContext = React.createContext({
  settings: [],
  styles: [],
  content: {}
})
export const StateContext = React.createContext([])
const EditingContext = React.createContext(true)

function reducer(value, action) {
  switch (action.type) {
    case "update_content":
      return { ...value, content: { ...value.content, ...action.content } }
    case "update_settings":
      return { ...value, settings: { ...value.settings, ...action.settings } }
    default:
      throw new Error(`Unknown action: ${action.type}`)
  }
}

export function useTemplateState() {
  return React.useContext(StateContext)
}

function createSetting(setting, value, dispatch) {
  const onChange = event => {
    dispatch({
      type: "update_settings",
      settings: { [setting.name]: event.target.value }
    })
  }

  const css = setting.css ? setting.css(value) : ""

  return {
    ...setting,
    value,
    onChange,
    css
  }
}

export function useSettings() {
  const [state, dispatch] = useTemplateState()

  const template = React.useContext(TemplateContext)

  return template.settings.map(setting =>
    createSetting(setting, state.settings[setting.name], dispatch)
  )
}

export function useOverride(name) {
  const overrides = React.useContext(OverridesContext)
  return overrides[name]
}

export function useIsEditing() {
  return React.useContext(EditingContext)
}

/**
 * Renders the parsed template JSON tree. All available components
 * can be found in "./components".
 */
const Element = ({ type, props, children }) => {
  if (!type) return null

  const Component = components[type] || type

  return (
    <Component {...props}>
      {children &&
        children.map((child, index) => {
          return typeof child === "string" ? (
            child
          ) : (
            <Element key={index} {...child} />
          )
        })}
    </Component>
  )
}

/**
 * Returns a function that can be called to get an object
 * representing the current state of the template editor in the form:
 * {
 *   html: "",
 *   content: {},
 *   settings: {}
 * }
 *
 * This function can be accessed by passing a ref into the `data`
 * prop of TemplateEditor. The reason the data is accessed this way
 * is to keep all of the state management logic inside
 * `TemplateEditor`, but be able to access it from a parent.
 */
export function createData({ template, state, overrides }) {
  return () => {
    const html = render(template, { state, overrides })

    let content = {}
    for (let key in state.content) {
      content[key] = state.content[key].toJS()
    }

    return {
      html,
      content,
      settings: state.settings
    }
  }
}

function render(template, { state, overrides }) {
  return replaceOutlookTags(
    ReactDOMServer.renderToStaticMarkup(
      <EditingContext.Provider value={false}>
        <StateContext.Provider value={[state, () => {}]}>
          <TemplateContext.Provider value={template}>
            <OverridesContext.Provider value={overrides}>
              <Element {...template.elements} />
            </OverridesContext.Provider>
          </TemplateContext.Provider>
        </StateContext.Provider>
      </EditingContext.Provider>
    )
  )
}

export function html(templateString, { content, settings, overrides }) {
  const template = parse(templateString, { content, settings })
  return render(template, { state: template.defaults, overrides })
}

function TemplateEditor({
  children,
  template: templateString,
  settings,
  content,
  data,
  overrides = {}
}) {
  const template = React.useMemo(
    () => parse(templateString, { content, settings }),
    [templateString]
  )

  const [state, dispatch] = useReducer(reducer, template.defaults)

  const context = React.useMemo(() => [state, dispatch], [state])

  React.useEffect(() => {
    if (data) {
      data.current = createData({ template, state, overrides })
    }
  })

  const theme = themeFromTemplateSettings(state.settings)

  return (
    <StateContext.Provider value={context}>
      <TemplateContext.Provider value={template}>
        <ThemeProvider theme={theme}>
          <OverridesContext.Provider value={overrides}>
            <ClosestEditableDetector>{children}</ClosestEditableDetector>
          </OverridesContext.Provider>
        </ThemeProvider>
      </TemplateContext.Provider>
    </StateContext.Provider>
  )
}

function Settings() {
  const settings = useSettings()

  return settings.map(setting => {
    const Component = setting.component
    return (
      <Component
        key={setting.name}
        onChange={setting.onChange}
        value={setting.value}
      />
    )
  })
}

function Content() {
  const template = React.useContext(TemplateContext)

  return (
    <EditorContent
      className="template-editor"
      location="template"
      style={{ width: "100%" }}
    >
      <Element {...template.elements} />
    </EditorContent>
  )
}

TemplateEditor.Content = Content
TemplateEditor.Settings = Settings
TemplateEditor.useSettings = useSettings
TemplateEditor.useTemplateState = useTemplateState

export default TemplateEditor
